/**
 * Feature-level access gate for the Solar Design tool (SD-2.1, FR-29; RBAC-6.2, FR-21).
 *
 * The PURE decision half of the access seam: given whether the account has Solar Design, whether
 * the person holds `design.use`, and whether they are a superadmin, decide whether they may use the
 * Design feature AT ALL. `useDesignFeatureAccess()` wires this to `PermissionContext` and the
 * account registry; the Proposals-tab Design section reads the hook.
 *
 * RBAC-6.2 retired `tenant_settings.design_access_role_ids` (20261103x2) and created `design.use`,
 * granted to Admin. Who may use Design is now chosen in Roles & Permissions, like everything else.
 *
 * Two load-bearing rules, identical to the Contracts gate:
 *   1. CLOSED-BY-DEFAULT / FAILS CLOSED — an account switch that is off or unreadable denies everyone
 *      but a superadmin. This matters more here than for Contracts, because Design SPENDS MONEY per
 *      resolve: a gate that failed open would bill a tenant for a feature nobody enabled.
 *   2. SUPERADMIN BYPASS — a superadmin is always allowed, so a misconfigured gate can never lock
 *      the people who administer it out of the very surface that fixes it.
 *
 * WHY THIS IS A DELIBERATE COPY OF `computeContractsFeatureAccess` RATHER THAN A SHARED HELPER.
 * These two gates are identical TODAY and are not required to stay identical. Design is metered and
 * pre-release; Contracts is shipped and unmetered. The first time one of them needs a per-project
 * condition, a quota interaction, or a different superadmin rule, a shared
 * `computeFeatureAccess(feature)` has to grow a branch, and a branch inside a security primitive is
 * read by everyone and understood by nobody.
 *
 * NOT modelled on `proposalsAccess.ts`, whose own docstring says it is not a permission model.
 *
 * Orthogonal to the FR-30 daily quota (backend `app/services/solar/quota.py`): that decides "may
 * this tenant afford another resolve today?"; this decides "may this user see Design at all?". A
 * surface must satisfy BOTH, and they are separate inputs on purpose.
 */

/** RBAC-6.2. The permission that lets a person use Design. Seeded by 20261103x2. */
export const DESIGN_USE_PERMISSION = 'design.use';

export interface DesignFeatureAccessInput {
  /**
   * RBAC-6.1 / FR-19. Whether Design is switched ON for the account
   * (`my_account_modules()`, KNK-1.55's registry). Required, so no caller can forget it. False or
   * null (unreadable) hides the feature from everyone but a superadmin.
   */
  accountEnabled: boolean | null | undefined;
  /** RBAC-6.2. `hasPermission(DESIGN_USE_PERMISSION)`. */
  holdsDesignUse: boolean;
  /** Superadmin bypass — always allowed regardless of the account. */
  isSuperadmin: boolean;
}

/** Decide feature-level Design access: a superadmin, or a holder of design.use in an account that has it. */
export function computeDesignFeatureAccess(input: DesignFeatureAccessInput): boolean {
  if (input.isSuperadmin) return true; // rule 2 — superadmin always allowed
  if (input.accountEnabled !== true) return false; // rule 1 — the account switch comes first
  return input.holdsDesignUse === true;
}
